import * as React from 'react';

export interface IFormContext {
  /**
   * Name to prefix form fields
   */
  name: string;
}

export interface IRadioContext {
  /**
   * Name to prefix RadioGroup
   */
  groupId: string;
  /**
   * Disables children inputs
   */
  groupDisabled?: boolean;
  /**
   * function Event listener that handles change
   */
  groupOnChange?: React.ChangeEventHandler<HTMLInputElement>;
}

export interface ICheckContext {
  /**
   * Disables children inputs
   */
  groupDisabled?: boolean;
  /**
   * function Event listener that handles change
   */
  groupOnChange?: React.ChangeEventHandler<HTMLInputElement>;
}

export const FormContext = React.createContext<IFormContext | undefined>(
  undefined
);

export const RadioGroupContext = React.createContext<IRadioContext>({
  groupId: '',
  groupDisabled: false,
  groupOnChange: undefined
});

export const CheckGroupContext = React.createContext<ICheckContext>({
  groupDisabled: false,
  groupOnChange: undefined
});

/**
 * Hook that returns context if used within a form, otherwise it throws
 * an error
 *
 * @return IFormContext
 */
export const useFormContext = (): IFormContext => {
  const context = React.useContext(FormContext);
  if (!context) {
    throw new Error('Must be used within a `Form`.');
  }
  return context;
};

/**
 * Hook that returns context if used within a RadioGroup, otherwise it throws
 * an error
 *
 * @return IRadioContext
 */
export const useRadioGroupContext = (): IRadioContext => {
  const context = React.useContext(RadioGroupContext);
  if (!context) {
    throw new Error('Must be used within a `RadioGroup`.');
  }
  return context;
};

/**
 * Hook that returns context if used within a CheckboxGroup, otherwise it throws
 * an error
 *
 * @return ICheckContext
 */
export const useCheckboxGroupContext = (): ICheckContext => {
  const context = React.useContext(CheckGroupContext);
  return context;
};
